import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import productData from "../Datas/productData";

const CART_STORAGE_KEY = "cartItems";
const WISHLIST_STORAGE_KEY = "wishlist";

const getStorage = () => {
  if (typeof window === "undefined") return null;

  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const readStoredArray = (key, fallbackKeys = []) => {
  const storage = getStorage();
  if (!storage) return [];

  try {
    const storedValue = [key, ...fallbackKeys]
      .map((storageKey) => storage.getItem(storageKey))
      .find((value) => value !== null);
    if (!storedValue) return [];

    const parsedValue = JSON.parse(storedValue);
    return Array.isArray(parsedValue) ? parsedValue : [];
  } catch {
    return [];
  }
};

const persistArray = (key, value) => {
  const storage = getStorage();
  if (!storage) return;

  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage can be unavailable in private browsing or when the quota is full.
  }
};

const normalizeProductId = (productOrId) => {
  const rawId =
    productOrId && typeof productOrId === "object"
      ? productOrId.id
      : productOrId;
  const numericId = Number(rawId);
  return Number.isFinite(numericId) ? numericId : null;
};

const findProduct = (productOrId) => {
  const productId = normalizeProductId(productOrId);
  return productData.find((product) => product.id === productId);
};

const normalizeCartItems = (items) =>
  items
    .map((item) => {
      const id = normalizeProductId(item);
      const quantity = Math.max(1, Math.floor(Number(item?.quantity) || 1));
      return {
        id,
        size: typeof item?.size === "string" ? item.size : "",
        color: typeof item?.color === "string" ? item.color : "",
        quantity,
      };
    })
    .filter((item) => item.id !== null && findProduct(item.id));

const normalizeWishlist = (items) => [
  ...new Set(
    items
      .map(normalizeProductId)
      .filter((id) => id !== null && findProduct(id))
  ),
];

export const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() =>
    normalizeCartItems(readStoredArray(CART_STORAGE_KEY))
  );
  const [wishlist, setWishlist] = useState(() =>
    normalizeWishlist(
      readStoredArray(WISHLIST_STORAGE_KEY, ["wishlistItems"])
    )
  );

  useEffect(() => {
    persistArray(CART_STORAGE_KEY, cartItems);
  }, [cartItems]);

  useEffect(() => {
    persistArray(WISHLIST_STORAGE_KEY, wishlist);
  }, [wishlist]);

  const addToCart = useCallback(
    (productOrId, quantity = 1, size, color) => {
      const product = findProduct(productOrId);
      if (!product || product.stock < 1) return;

      const productId = product.id;
      const selectedSize = size || product.size?.[0] || "";
      const selectedColor = color || product.colors?.[0] || "";
      const amount = Math.max(1, Math.floor(Number(quantity) || 1));

      setCartItems((currentItems) => {
        const matchingItem = currentItems.find(
          (item) =>
            item.id === productId &&
            item.size === selectedSize &&
            item.color === selectedColor
        );

        if (!matchingItem) {
          return [
            ...currentItems,
            {
              id: productId,
              size: selectedSize,
              color: selectedColor,
              quantity: Math.min(amount, product.stock),
            },
          ];
        }

        return currentItems.map((item) =>
          item.id === productId &&
          item.size === selectedSize &&
          item.color === selectedColor
            ? {
                ...item,
                quantity: Math.min(item.quantity + amount, product.stock),
              }
            : item
        );
      });
    },
    []
  );

  const removeFromCart = useCallback((productOrId, size, color) => {
    const productId = normalizeProductId(productOrId);
    if (productId === null) return;

    setCartItems((currentItems) =>
      currentItems.filter((item) => {
        if (item.id !== productId) return true;
        if (size !== undefined && item.size !== size) return true;
        if (color !== undefined && item.color !== color) return true;
        return false;
      })
    );
  }, []);

  const updateQuantity = useCallback(
    (productOrId, size, color, quantity) => {
      const product = findProduct(productOrId);
      if (!product) return;

      const nextQuantity = Math.min(
        Math.max(1, Math.floor(Number(quantity) || 1)),
        product.stock
      );

      setCartItems((currentItems) =>
        currentItems.map((item) =>
          item.id === product.id && item.size === size && item.color === color
            ? { ...item, quantity: nextQuantity }
            : item
        )
      );
    },
    []
  );

  const clearCart = useCallback(() => {
    setCartItems([]);
  }, []);

  const addToWishlist = useCallback((productOrId) => {
    const product = findProduct(productOrId);
    if (!product) return;

    setWishlist((currentWishlist) =>
      currentWishlist.includes(product.id)
        ? currentWishlist
        : [...currentWishlist, product.id]
    );
  }, []);

  const removeFromWishlist = useCallback((productOrId) => {
    const productId = normalizeProductId(productOrId);
    if (productId === null) return;

    setWishlist((currentWishlist) =>
      currentWishlist.filter((id) => id !== productId)
    );
  }, []);

  const toggleWishlist = useCallback((productOrId) => {
    const product = findProduct(productOrId);
    if (!product) return;

    setWishlist((currentWishlist) =>
      currentWishlist.includes(product.id)
        ? currentWishlist.filter((id) => id !== product.id)
        : [...currentWishlist, product.id]
    );
  }, []);

  const clearWishlist = useCallback(() => {
    setWishlist([]);
  }, []);

  const isInWishlist = useCallback(
    (productOrId) => {
      const productId = normalizeProductId(productOrId);
      return productId !== null && wishlist.includes(productId);
    },
    [wishlist]
  );

  const cartCount = useMemo(
    () => cartItems.reduce((total, item) => total + item.quantity, 0),
    [cartItems]
  );

  const cartSubtotal = useMemo(
    () =>
      cartItems.reduce((total, item) => {
        const product = findProduct(item.id);
        return product ? total + product.price * item.quantity : total;
      }, 0),
    [cartItems]
  );

  const wishlistItems = useMemo(
    () =>
      wishlist
        .map((productId) => findProduct(productId))
        .filter(Boolean),
    [wishlist]
  );

  const contextValue = useMemo(
    () => ({
      cartItems,
      wishlist,
      wishlistIds: wishlist,
      wishlistItems,
      cartCount,
      cartSubtotal,
      wishlistCount: wishlist.length,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      addToWishlist,
      removeFromWishlist,
      toggleWishlist,
      clearWishlist,
      isInWishlist,
      isWishlisted: isInWishlist,
      addWishlist: addToWishlist,
      removeWishlist: removeFromWishlist,
    }),
    [
      addToCart,
      addToWishlist,
      cartCount,
      cartItems,
      cartSubtotal,
      clearCart,
      clearWishlist,
      isInWishlist,
      removeFromCart,
      removeFromWishlist,
      toggleWishlist,
      updateQuantity,
      wishlist,
      wishlistItems,
    ]
  );

  return (
    <CartContext.Provider value={contextValue}>
      {children}
    </CartContext.Provider>
  );
};
